import { m } from 'framer-motion';
// @mui
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, Card, Container, Grid, Hidden, Typography } from '@mui/material';
// components
import { MotionViewport, varFade } from '../../components/animate';

// ----------------------------------------------------------------------

const CUSTOMER_ADVANTAGES = [
  'Bestellen bei seiner Lieblingsbäckerei',
  'Keine Wartezeiten',
  'Schnelle, komfortable Bezahlung',
  'Produktinformationen im Überblick',
  'Produkte immer verfügbar',
];

const BAKER_ADVANTAGES = [
  'Differenzierung von anderen Bäckereien',
  'Bestellübersicht',
  'Produktanalysen',
  'Plaungssicherheit',
  'Kosteneinsparung',
];

// ----------------------------------------------------------------------

const shadowIcon = (color: string) => `drop-shadow(2px 2px 2px ${alpha(color, 0.48)})`;

const RootStyle = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.neutral,
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(15),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  justifyContent: 'center'
}));

const AdvantagesStyle = styled('div')(({ theme }) => {
  const shadowCard = (opacity: number) =>
    theme.palette.mode === 'light'
      ? alpha(theme.palette.grey[500], opacity)
      : alpha(theme.palette.common.black, opacity);

  return {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-around',
    [theme.breakpoints.down('md')]: {
      minHeight: 440,
      minWidth: 380,
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
      boxShadow: `-40px 40px 80px 0 ${shadowCard(0.4)}`,
      padding: theme.spacing(10, 5, 5),
      borderRadius: '16px',
    },
  }
});

const AdvantageStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center'
  }
}));

const AdvantageIconStyle = styled('img')(({ theme }) => ({
  height: '36px',
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
}));

const IconStyle = styled('img')(({ theme }) => ({
  width: '64px',
  height: 'auto',
  marginBottom: theme.spacing(10),
}));

const ImageStyle = styled('img')(({ theme }) => ({
  height: 'auto',
  paddingRight: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  filter: shadowIcon('#000000'),
}));

// ----------------------------------------------------------------------

export default function HomeBakerBenefits() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  return (
    <RootStyle>
      <Container component={MotionViewport} maxWidth="xl">
        <Box
          sx={{
            textAlign: 'center',
            mb: 10,
          }}
        >
          <m.div variants={varFade().inUp}>
            <Typography component="div" variant="overline" sx={{ mb: 2, color: 'text.disabled' }}>
            Onlineshop
            </Typography>
          </m.div>
          <m.div variants={varFade().inDown}>
            <Typography variant="h2">Mit Shop</Typography>
          </m.div>
        </Box>

        <Hidden mdDown>
          <Grid container sx={{ mb: 4 }}>
            <Grid item xs={6}>
              <Typography textAlign="center" variant='h5' paragraph>
                Vorteile für den Bäcker:
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography textAlign="center" variant='h5' paragraph>
                Vorteile für den Kunden:
              </Typography>
            </Grid>
          </Grid>
        </Hidden>

        <Grid container spacing={{ xs: 3, md: 0.1 }}>
          <Grid item xs={12} md={5}>
            <ContentStyle sx={{ ...{justifyContent: { md: 'flex-end' }} }}>
              <AdvantagesStyle sx={{ ...{alignItems: { md: 'flex-end'}} }}>
                <Hidden mdUp>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <IconStyle src="static/home/baker.webp" alt="" sx={{...{filter: shadowIcon('#F3F5E6')}}} />
                    <Typography textAlign="center" variant='h5' paragraph>
                      Vorteile für den Bäcker:
                    </Typography>
                  </Box>
                </Hidden>
                {BAKER_ADVANTAGES.map((advantage, index) => (
                  <AdvantageStyle key={index}>
                    <Typography sx={{ color: isLight ? 'text.secondary' : 'common.white', textAlign: { md: 'end' } }}>
                      {advantage}
                    </Typography>
                    <Hidden mdDown>
                      <AdvantageIconStyle src="static/home/advantage.webp" alt="" />
                    </Hidden>
                  </AdvantageStyle>
                ))}
              </AdvantagesStyle>
            </ContentStyle>
          </Grid>

          <Grid item md={2}>
            <Hidden mdDown>
              <ImageStyle src="static/home/order.webp" alt="" />
            </Hidden>
          </Grid>

          <Grid item xs={12} md={5}>
            <ContentStyle sx={{ ...{justifyContent: { md: 'flex-start' }} }}>
              <AdvantagesStyle>
                <Hidden mdUp>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <IconStyle src="static/home/girl.webp" alt="" sx={{...{filter: shadowIcon('#CE857A')}}} />
                    <Typography textAlign="center" variant='h5' paragraph>
                      Vorteile für den Kunden:
                    </Typography>
                  </Box>
                </Hidden>
                {CUSTOMER_ADVANTAGES.map((advantage, index) => (
                  <AdvantageStyle key={index}>
                    <Hidden mdDown>
                      <AdvantageIconStyle src="static/home/advantage.webp" alt="" />
                    </Hidden>
                    <Typography sx={{ color: isLight ? 'text.secondary' : 'common.white' }}>
                      {advantage}
                    </Typography>
                  </AdvantageStyle>
                ))}
              </AdvantagesStyle>
            </ContentStyle>
          </Grid>
        </Grid>
      </Container>
    </RootStyle>
  );
}
